import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBankSettings } from "@/hooks/useBankSettings";
import { useFxRate } from "@/hooks/useFxRate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Upload, Copy, Check, Building2 } from "lucide-react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  planId: string;
  planName: string;
  amount: number;
  onSubmitted?: () => void;
};

export function BankTransferDialog({ open, onOpenChange, planId, planName, amount, onSubmitted }: Props) {
  const { user } = useAuth();
  const { settings, loading: settingsLoading } = useBankSettings();
  const { rate: nprRate, loading: fxLoading, fetchedAt: fxFetchedAt } = useFxRate("NPR");
  const [file, setFile] = useState<File | null>(null);
  const [senderName, setSenderName] = useState("");
  const [txnRef, setTxnRef] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const nprAmount = nprRate ? amount * nprRate : null;
  const nprAmountStr = nprAmount
    ? `Rs ${nprAmount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
    : null;

  const copy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!file) {
      toast.error("Please upload your payment receipt");
      return;
    }
    setSubmitting(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${user.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("payment-receipts")
        .upload(path, file, { upsert: false });
      if (upErr) throw upErr;

      const { error: insErr } = await supabase.from("manual_payments").insert({
        user_id: user.id,
        user_email: user.email,
        plan_id: planId,
        amount,
        receipt_url: path,
        sender_name: senderName || null,
        transaction_ref: txnRef || null,
        contact_phone: phone || null,
        user_notes: notes || null,
        duration_days: settings.duration_days,
      });
      if (insErr) throw insErr;

      toast.success("Payment submitted!", {
        description: "We'll review your receipt and activate your plan shortly.",
      });
      onOpenChange(false);
      setFile(null);
      setSenderName("");
      setTxnRef("");
      setPhone("");
      setNotes("");
      onSubmitted?.();
    } catch (err) {
      toast.error("Could not submit payment", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const Field = ({ label, value, field }: { label: string; value: string; field: string }) => {
    if (!value) return null;
    return (
      <div className="flex items-center justify-between py-2 border-b last:border-0 gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-sm font-medium truncate">{value}</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => copy(value, field)}>
          {copiedField === field ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" /> Pay with Bank Transfer - {planName}
          </DialogTitle>
          <DialogDescription>
            Transfer the exact amount, then upload your receipt below. Your plan will activate after admin approval.
          </DialogDescription>
        </DialogHeader>

        {settingsLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !settings.enabled || !settings.account_number ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            Bank transfer is not configured yet. Please use card checkout or contact support.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-start justify-between mb-2 gap-3">
                <h3 className="font-semibold text-sm">Bank Details</h3>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Amount to pay</p>
                  <p className="text-lg font-bold leading-tight">${amount.toFixed(2)}</p>
                  {fxLoading ? (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end mt-0.5">
                      <Loader2 className="h-3 w-3 animate-spin" /> fetching NPR rate…
                    </p>
                  ) : nprAmountStr ? (
                    <>
                      <p className="text-base font-semibold text-primary leading-tight">
                        ≈ {nprAmountStr}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Live rate: 1 USD = Rs {nprRate?.toFixed(2)}
                        {fxFetchedAt ? "" : " (fallback)"}
                      </p>
                      <button
                        type="button"
                        onClick={() => copy(String(Math.round(nprAmount!)), "npr")}
                        className="text-[10px] text-primary hover:underline inline-flex items-center gap-1"
                      >
                        {copiedField === "npr" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                        Copy NPR amount
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Field label="Bank Name" value={settings.bank_name} field="bank" />
                  <Field label="Account Name" value={settings.account_name} field="name" />
                  <Field label="Account Number" value={settings.account_number} field="acct" />
                  <Field label="IBAN" value={settings.iban} field="iban" />
                  <Field label="SWIFT" value={settings.swift} field="swift" />
                </div>
                {settings.qr_image_url && (
                  <div className="flex flex-col items-center justify-center">
                    <p className="text-xs text-muted-foreground mb-2">Scan QR to pay</p>
                    <img
                      src={settings.qr_image_url}
                      alt="Payment QR"
                      className="w-64 h-64 md:w-72 md:h-72 object-contain bg-white rounded-md p-3 border shadow-sm"
                    />
                  </div>
                )}
              </div>
              {settings.instructions && (
                <p className="text-xs text-muted-foreground mt-3 whitespace-pre-wrap">{settings.instructions}</p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="receipt">Payment Receipt *</Label>
                <Input
                  id="receipt"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  required
                  className="mt-1"
                />
                {file && <p className="text-xs text-muted-foreground mt-1">{file.name}</p>}
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="sender">Sender Name</Label>
                  <Input id="sender" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Name on the transfer" />
                </div>
                <div>
                  <Label htmlFor="txn">Transaction / Reference ID</Label>
                  <Input id="txn" value={txnRef} onChange={(e) => setTxnRef(e.target.value)} placeholder="Bank reference number" />
                </div>
              </div>
              <div>
                <Label htmlFor="phone">Phone / WhatsApp</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="So we can reach you if needed" />
              </div>
              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
              </div>
              <Button type="submit" disabled={submitting || !file} className="w-full">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Submit Payment for Review
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
